import { useState, type FC, useMemo } from 'react';
import {
  UiDrawer,
  UiDrawerOverlay,
  UiDrawerContent,
  UiDrawerBody,
  UiDrawerCloseButton,
  type UiDrawerProps,
  UiStack,
  UiButton,
  UiHStack,
  UiText,
  uiStyles
} from '@/lib/ui';
import BaseMessageBarInfo from '@/base/MessageBar/Info';
import BaseDividerHorizontal from '@/base/Divider/Horizontal';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { preparePayment, type PaymentIntent, type PreparePaymentRequest } from '@/api/registration';
import { type ApiResponse } from '@/api/tenantClient';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import QueryContainer from '@/base/QueryContainer/QueryContainer';
import BaseMessageBarError from '@/base/MessageBar/Error';
import { useRegisterGroupSteps } from '@/registration/hook/useRegisterGroupSteps';
import { useGroupCartQuery } from '@/registration/hook/useGroupCartQuery';
import Cart from '@/registration/component/Register/GroupShoppingCart/Cart';

export interface PaymentByInvoiceDrawerProps extends Omit<UiDrawerProps, 'children'> {
}

const PaymentByInvoiceDrawer: FC<PaymentByInvoiceDrawerProps> = ({
  ...props
}) => {
  const queryClient = useQueryClient();
  const { createTenantApiRequest } = useTenantApi();
  const [errors, setErrors] = useState<string[]>([]);

  const { eventId } = useRegisterRoute();
  const { toNextStep } = useRegisterGroupSteps();

  const cartQuery = useGroupCartQuery(eventId);
  const cartItemErrors = useMemo(() => {
    let cartErrors: string[] = [];
    cartQuery.data?.cartItems.forEach((cartItem) => {
      cartErrors = cartErrors.concat(cartItem.errors ?? []);
    });
    return cartErrors;
  }, [cartQuery.data?.cartItems]);

  const { mutate: preparePaymentMutate, isLoading: isPreparePaymentLoading } = useMutation<ApiResponse<PaymentIntent>, Error, PreparePaymentRequest>({
    mutationFn: async (preparePaymentData: PreparePaymentRequest) => {
      return await preparePayment(createTenantApiRequest)(preparePaymentData) as ApiResponse<PaymentIntent>;
    },
    onSuccess: (order) => {
      const orderResponse = order.items?.[0].order;
      if (orderResponse) {
        toNextStep({ additionalParams: { orderHash: orderResponse.secureHash } });
      }
    },
    onError: (error: any) => {
      setErrors([error.message ?? 'Failed to save the host.']);
    }
  });

  const onSubmit = () => {
    if (!cartQuery.data) {
      return;
    }
    const preparePaymentData: PreparePaymentRequest = {
      cartId: cartQuery.data.id,
      locale: 'aud',
      gateway: 'invoice'
    };
    preparePaymentMutate(preparePaymentData);
  };

  return (
    <UiDrawer placement={'right'} size={'xl'} {...props}>
      <UiDrawerOverlay />
      <UiDrawerContent bgColor={'gray.100'}>
        <UiDrawerCloseButton size={'lg'} fontWeight={'bold'} color={'primary.500'} />
        <QueryContainer query={cartQuery}>
          {cart => {
            return (
              <UiDrawerBody px={0} pb={16} alignItems={'stretch'}>
                {errors.length > 0 && (
                  <UiStack spacing={4} flexGrow={1} py={4}>
                    {errors.map((error) => {
                      return (
                        <BaseMessageBarError key={error}>
                          {error}
                        </BaseMessageBarError>
                      );
                    })}
                  </UiStack>
                )}
                <UiStack
                  px={{ base: 4, lg: 8 }}
                  py={8}
                  flexGrow={1}
                  spacing={6}
                >
                  <UiText variant={'title'}>Pay by invoice</UiText>
                  <BaseMessageBarInfo
                    borderRadius={uiStyles.borderRadius}
                    bgColor={'#fff'}
                  >
                  We will send you an email with payment details.
                  </BaseMessageBarInfo>
                  <BaseDividerHorizontal height={2} />
                  <Cart showErrors={true} />
                </UiStack>
                <UiHStack px={{ base: 4, lg: 8 }} justifyContent={'flex-end'}>
                  <UiButton
                    px={8}
                    size={'lg'}
                    shadow={'base'}
                    colorScheme={'primary'}
                    onClick={onSubmit}
                    isDisabled={cart.cartItems.length < 1 || cartItemErrors.length > 0}
                    isLoading={isPreparePaymentLoading}>
                  Confirm
                  </UiButton>
                </UiHStack>
              </UiDrawerBody>
            );
          }}
        </QueryContainer>
      </UiDrawerContent>
    </UiDrawer>
  );
};

export default PaymentByInvoiceDrawer;
